import type SecureLs from "secure-ls";
import { useAppStore } from "./app-store";

/** 旧 vuex-persistedstate が単一キー 'vuex' に集約していた永続化ブロブの型 */
type LegacyPersistedState = {
  lastAccessPath?: string;
  searchParam?: { word: string };
  fromQuery?: string;
  device?: string;
  invokerapp?: string;
  user_id?: string;
  randomNumber1?: number[];
  randomNumber2?: number[];
  randomNumber3?: number[];
};

const LEGACY_KEY = "vuex";
const MIGRATED_FLAG = "pinia_migrated";

/**
 * 旧 vuex-persistedstate（単一キー 'vuex'）の永続化データを、
 * Pinia(useAppStore, キー 'app') へ一度だけ詰め替える移行シム。
 *
 * - `randomNumber1〜3` は IndexedDB 暗号化(AES-GCM)の鍵導出ソルト。失うと既存キャッシュが復号不能になる。
 * - `device` / `invokerapp` / `user_id` / `fromQuery` はネイティブ起動クエリ由来の永続値。失うとモバイル判定等が誤動作する。
 *
 * 二重実行は `pinia_migrated` フラグで防止する。全ユーザーが新版へ移行後（数リリース後）に本シムと旧キーは削除可能。
 *
 * 注意: 呼び出し前に `setActivePinia(pinia)` を済ませ、`useAppStore()` が setup 外で使える状態にしておくこと。
 */
export function migrateLegacyPersistedState(ls: SecureLs): void {
  try {
    if (ls.get(MIGRATED_FLAG)) return; // 二重実行防止（重要）

    const raw = ls.get(LEGACY_KEY); // 旧 vuex-persistedstate ブロブ
    // SecureLs + vuex-persistedstate の二段シリアライズにより、返り値が
    // JSON 文字列 / オブジェクトのどちらにもなりうるため両対応する。
    const legacy: LegacyPersistedState | null =
      typeof raw === "string"
        ? raw
          ? (JSON.parse(raw) as LegacyPersistedState)
          : null
        : (raw as LegacyPersistedState | null);

    if (legacy) {
      useAppStore().$patch({
        randomNumber1: legacy.randomNumber1 ?? [], // 暗号化ソルト（最重要）
        randomNumber2: legacy.randomNumber2 ?? [],
        randomNumber3: legacy.randomNumber3 ?? [],
        device: legacy.device ?? "",
        invokerapp: legacy.invokerapp ?? "",
        user_id: legacy.user_id ?? "",
        fromQuery: legacy.fromQuery ?? "",
        lastAccessPath: legacy.lastAccessPath ?? "",
        searchParam: legacy.searchParam ?? { word: "" },
      });
    }

    ls.set(MIGRATED_FLAG, true);
    // ls.remove(LEGACY_KEY); ← ロールバック保険のため数リリース後に掃除する
  } catch (error) {
    // 移行失敗でアプリ起動自体は止めない（永続値は既定値で続行）
    console.error("migrateLegacyPersistedState error:%O", error);
  }
}
